import { supabase } from '@/lib/supabase';
import type { AuditLog, LogActionInput, AuditLogFilters } from '@/types/audit-log.types';

/**
 * Service pour enregistrer les actions dans les journaux d'audit
 */

/**
 * Enregistre une action dans les journaux d'audit
 * Capture automatiquement les informations utilisateur et contexte
 */
export async function logAction(input: LogActionInput): Promise<void> {
  console.log('[AUDIT] logAction called', { action: input.action, entity_type: input.entity_type, entity_id: input.entity_id });
  try {
    // Récupérer l'utilisateur actuel
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.warn('[AUDIT] Cannot log action: no authenticated user');
      return;
    }

    // Récupérer les informations du profil utilisateur
    const { data: profile } = await supabase
      .from('profiles')
      .select('first_name, last_name, role, company_id')
      .eq('id', user.id)
      .single();

    if (!profile) {
      console.warn('[AUDIT] Cannot log action: user profile not found', { user_id: user.id });
      return;
    }

    // Récupérer les informations de la company si présente
    let companyName = null;
    if (profile.company_id) {
      const { data: company } = await supabase
        .from('companies')
        .select('name')
        .eq('id', profile.company_id)
        .single();

      companyName = company?.name || null;
    }

    // Construire le nom complet de l'utilisateur
    const userName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Utilisateur inconnu';

    // Préparer les données d'audit
    const auditData = {
      user_id: user.id,
      user_name: userName,
      user_email: user.email || '',
      user_role: profile.role || 'unknown',
      company_id: profile.company_id || null,
      company_name: companyName,
      action: input.action,
      entity_type: input.entity_type,
      entity_id: input.entity_id || null,
      entity_name: input.entity_name || null,
      details: input.details || null,
      old_value: input.old_value || null,
      new_value: input.new_value || null,
      metadata: input.metadata || {},
      // Note: ip_address et user_agent peuvent être capturés côté serveur via une edge function
      // Pour l'instant, on les laisse null car ils nécessitent un contexte serveur
      ip_address: null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    };

    // Insérer dans la table activity_logs
    const { error } = await supabase
      .from('activity_logs')
      .insert(auditData);

    if (error) {
      console.error('[AUDIT] Error inserting activity_logs row:', error, auditData);
      throw error;
    }
    console.log('[AUDIT] logAction inserted', { action: input.action, entity_type: input.entity_type });
  } catch (error) {
    console.error('[AUDIT] Failed to log action:', error);
    // Ne pas faire échouer l'opération principale si le logging échoue
  }
}

/**
 * Récupère les journaux d'audit avec filtres optionnels
 * Réservé aux administrateurs
 */
export async function getAuditLogs(filters?: AuditLogFilters): Promise<AuditLog[]> {
  try {
    let query = supabase
      .from('activity_logs')
      .select('*')
      .order('created_at', { ascending: false });

    // Appliquer les filtres
    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate);
    }
    if (filters?.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters?.action) {
      query = query.eq('action', filters.action);
    }
    if (filters?.entityType) {
      query = query.eq('entity_type', filters.entityType);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch audit logs:', error);
    throw error;
  }
}

/**
 * Récupère le nombre total de logs d'audit
 */
export async function getAuditLogsCount(filters?: AuditLogFilters): Promise<number> {
  try {
    let query = supabase
      .from('activity_logs')
      .select('*', { count: 'exact', head: true });

    // Appliquer les filtres
    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate);
    }
    if (filters?.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters?.action) {
      query = query.eq('action', filters.action);
    }
    if (filters?.entityType) {
      query = query.eq('entity_type', filters.entityType);
    }

    const { count, error } = await query;

    if (error) {
      console.error('Error fetching audit logs count:', error);
      throw error;
    }

    return count || 0;
  } catch (error) {
    console.error('Failed to fetch audit logs count:', error);
    return 0;
  }
}

/**
 * Récupère les utilisateurs uniques ayant des logs d'audit
 * Utile pour le filtre par utilisateur
 */
export async function getAuditLogUsers(): Promise<Array<{ id: string; name: string; email: string; role: string }>> {
  try {
    const { data, error } = await supabase
      .from('activity_logs')
      .select('user_id, user_name, user_email, user_role')
      .order('user_name');

    if (error) {
      console.error('Error fetching audit log users:', error);
      throw error;
    }

    if (!data) return [];

    // Dédupliquer par user_id
    const uniqueUsers = new Map<string, { id: string; name: string; email: string; role: string }>();

    data.forEach(log => {
      if (!uniqueUsers.has(log.user_id)) {
        uniqueUsers.set(log.user_id, {
          id: log.user_id,
          name: log.user_name,
          email: log.user_email,
          role: log.user_role,
        });
      }
    });

    return Array.from(uniqueUsers.values());
  } catch (error) {
    console.error('Failed to fetch audit log users:', error);
    return [];
  }
}
